import React from "react";
import PropTypes from "prop-types";
import {
  Bookmark,
  BookmarkX,
  CircleHelp,
  ClipboardCheck,
  FileText,
  Heart,
  MonitorX,
  Star,
  Trash2,
  UserCheck,
} from "lucide-react";

import {
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
} from "lookup";
import { PROFILE_TEXT_SM } from "components/UserCardList/components/UserCardResult/constants";
import { MATCH_STATUS_TITLES } from "components/UserCardList/constants/ctaTitles";
import { cn } from "utils";

const DEFAULT_STYLES =
  "flex justify-center w-min px-2 items-center gap-1.5 text-sm font-bold font-inter text-grey-900 whitespace-nowrap border-l-4";

const STATUS_ICON_BY_TYPE = {
  bookmark: Bookmark,
  bookmarkX: BookmarkX,
  circleHelp: CircleHelp,
  clipboardCheck: ClipboardCheck,
  fileText: FileText,
  heart: Heart,
  monitorX: MonitorX,
  star: Star,
  trash2: Trash2,
  userCheck: UserCheck,
};

const StatusIcon = ({ filled, type }) => {
  const Icon = STATUS_ICON_BY_TYPE[type];

  if (!Icon) {
    return null;
  }

  return (
    <Icon
      size={16}
      strokeWidth={2}
      className="shrink-0 text-[#334155]"
      fill={filled ? "currentColor" : "none"}
    />
  );
};

const Status = ({ matchStatus, variant }) => {
  const renderStatus = (status) => {
    const statusTitle = MATCH_STATUS_TITLES[status];

    if (!statusTitle) {
      return null;
    }

    const label = statusTitle?.label;
    const icon = (
      <StatusIcon filled={statusTitle?.filledIcon} type={statusTitle?.icon} />
    );

    if (variant === "inline") {
      return (
        <span
          className={cn(
            "inline-flex min-w-0 flex-wrap items-center gap-2",
            PROFILE_TEXT_SM
          )}
          title={statusTitle.title}
        >
          {icon}
          {label}
        </span>
      );
    }

    switch (status) {
      case JOB_APPLICATION_MATCH_STATUS.APPLIED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-blue-800")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.ACCEPTED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "!text-grey-900")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.REJECTEDBYCUSTOMER: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-red-600")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-red-600")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMEMBER: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-red-600")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.MOREINFO: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-electricBlue")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.MATCHED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-l-4 border-green-500")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.OFFERSENT: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-[#C7D2FE] flex gap-2")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.SHORTLISTED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-blue-500")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.PRESHORTLISTED:
      case JOB_APPLICATION_MATCH_STATUS.TKSHORTLISTED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-cyan-300")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.SKIPPED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-orange-500")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.PASSEDON: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-orange-500")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.DELETED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-red-600")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "!text-grey-900")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.INTERESTED: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-bluepurple")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }
      case JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK: {
        return (
          <div
            className={cn(DEFAULT_STYLES, "border-bluepurple")}
            title={statusTitle.title}
          >
            {icon}
            <p>{label}</p>
          </div>
        );
      }

      default:
        return <></>;
    }
  };

  if (!matchStatus) {
    return null;
  }

  return <>{renderStatus(matchStatus)}</>;
};

Status.propTypes = {
  matchStatus: PropTypes.string,
  variant: PropTypes.oneOf(["default", "inline"]),
};

Status.defaultProps = {
  matchStatus: "",
  variant: "default",
};

StatusIcon.propTypes = {
  filled: PropTypes.bool,
  type: PropTypes.string,
};

StatusIcon.defaultProps = {
  filled: false,
  type: "",
};

export default Status;
